/** Controls the detached map popup and synchronizes platform/tile-layer updates. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GeoJsonObject } from 'geojson';
import leafletUmdScript from 'leaflet/dist/leaflet.js?raw';
import uavMapControlUmdScript from '@impleotv-pm/uav-map-control/dist/uav-map-control.umd.js?raw';
import { resolveTileLayersJson, sanitizeTileLayersJson } from './tileLayers';

// Runtime controlled by parent. planeIconPngUrl no longer needed directly here.

export interface MapPlatformDescriptor {
    id: string;
    geojson: GeoJsonObject;
    data?: unknown;
}

export interface MapAnnotationDescriptor {
    id: string;
    geojson: GeoJsonObject;
}

export interface MapMarkerPosition {
    lat: number;
    lon: number;
}

export type MapMarkerMode = 'master' | 'slave';

export interface MapMarkerToggleEvent {
    enabled: boolean;
    position: MapMarkerPosition | null;
}

export interface MapViewportDescriptor {
    center: MapMarkerPosition;
    zoom: number;
    bbox: [number, number, number, number];
}

interface MapWindowProps {
    open: boolean;
    onClose: () => void;
    /** Collection of platforms to display (diffed). */
    platforms: MapPlatformDescriptor[];
    /** Collection of annotations to display (diffed). */
    annotations?: MapAnnotationDescriptor[];
    /** JSON string describing available tile layers. */
    tileLayers?: string | null;
    /** Access token included only in requests to SkyView's local tile proxy. */
    mapTileAccessToken?: string | null;
    /** Whether draw controls should be shown in the popup map. */
    showDrawControls?: boolean;
    /** Current marker position that should be mirrored into the popup map. */
    markerPosition?: MapMarkerPosition | null;
    /** Whether the map-side marker should own interaction or follow the overlay. */
    markerMode?: MapMarkerMode;
    /** Fired when the popup map marker is dragged by the user. */
    onMarkerMove?: (position: MapMarkerPosition) => void;
    /** Fired when the popup map marker control is toggled by the user. */
    onMarkerToggle?: (event: MapMarkerToggleEvent) => void;
    /** Fired when the popup/direct map viewport changes while open. */
    onViewportChange?: (viewport: MapViewportDescriptor | null) => void;
}

const MAP_WINDOW_FEATURES = 'width=800,height=600,left=200,top=200';
const MAP_WINDOW_NAME = 'stanag-map-window';
const MAP_CHANNEL_PREFIX = 'stanag-map-window-channel';

const MAP_WINDOW_PAGE = 'map-window.html';
const MAP_WINDOW_ROOT_ID = 'stanag-map-window-root';
const CLOSE_POLL_INTERVAL_MS = 500;
const FIREFOX_FOCUS_PARENT_DELAY_MS = 40;
const FIREFOX_FOCUS_POPUP_DELAY_MS = 120;
const FIREFOX_FOCUS_FINAL_DELAY_MS = 220;
const VIEWPORT_POLL_INTERVAL_MS = 3000;

const createChannelName = () => `${MAP_CHANNEL_PREFIX}-${Math.random().toString(36).slice(2)}`;

const resolveMapWindowUrl = () => {
    const basePath = import.meta.env.BASE_URL ?? '/';
    const baseUrl = new URL(basePath, window.location.origin);
    return new URL(MAP_WINDOW_PAGE, baseUrl);
};

const buildDesktopPopupDocument = () => `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Map</title>
  <style>
    html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: #111827; }
    #${MAP_WINDOW_ROOT_ID} { width: 100%; height: 100%; display: block; }
    #${MAP_WINDOW_ROOT_ID} > uav-map { width: 100%; height: 100%; display: block; }
    #${MAP_WINDOW_ROOT_ID} .map-error { box-sizing: border-box; width: 100%; height: 100%; padding: 16px; color: #f9fafb; font-family: Segoe UI, Arial, sans-serif; }
    #${MAP_WINDOW_ROOT_ID} .map-error pre { margin: 0; white-space: pre-wrap; opacity: 0.9; }
  </style>
</head>
<body>
  <div id="${MAP_WINDOW_ROOT_ID}"></div>
</body>
</html>`;

type MapWindowCommandMessage =
    | {
          type: 'initialize';
          payload: {
              showDrawControls?: boolean;
              tileLayers?: string;
          };
      }
    | { type: 'add-platform'; payload: { id: string; geojson: GeoJsonObject; data?: unknown } }
    | { type: 'remove-platform'; payload: { id: string } }
    | { type: 'update-platform'; payload: { id: string; geojson: GeoJsonObject; data?: unknown } }
    | { type: 'update-platform-data'; payload: { id: string; data: unknown } }
    | { type: 'add-annotation'; payload: { id: string; geojson: GeoJsonObject } }
    | { type: 'update-annotation'; payload: { id: string; geojson: GeoJsonObject } }
    | { type: 'remove-annotation'; payload: { id: string } }
    | { type: 'teardown' }
    | { type: 'close' }
    | { type: 'set-tile-layers'; payload: { tileLayers?: string } }
    | { type: 'update-marker-position'; payload: MapMarkerPosition }
    | { type: 'set-marker-master'; payload: { master: boolean } };


type MapWindowEventMessage =
    | { type: 'ready' }
    | { type: 'popup-unloaded' }
    | { type: 'marker-move'; payload: MapMarkerPosition }
    | { type: 'marker-toggle'; payload: MapMarkerToggleEvent };
type DirectMapElement = HTMLElement & {
    AddPlatform?: (id: string, geojson: GeoJsonObject, data?: unknown) => void;
    RemovePlatform?: (id: string) => void;
    UpdatePlatform?: (id: string, geojson: GeoJsonObject, data?: unknown) => void;
    UpdatePlatformData?: (id: string, data: unknown) => void;
    AddAnnotation?: (id: string, geojson: GeoJsonObject) => void;
    UpdateAnnotation?: (id: string, geojson: GeoJsonObject) => void;
    RemoveAnnotation?: (id: string) => void;
    ShowDrawControls?: () => void;
    SetTileLayers?: (tileLayersJson: string) => void;
    UpdateMarkerPosition?: (lat: number, lon: number) => void;
    SetMarkerMaster?: (master: boolean) => void;
    SetAreaCoverageSearchVisible?: (visible: boolean) => void;
    GetViewport?: () => {
        center?: { lat?: number; lng?: number } | null;
        zoom?: number;
        bounds?: {
            getWest?: () => number;
            getSouth?: () => number;
            getEast?: () => number;
            getNorth?: () => number;
        } | null;
    } | null;
};

type MapViewportSnapshot = {
    center?: { lat?: number; lng?: number } | null;
    zoom?: number;
    bounds?: {
        getWest?: () => number;
        getSouth?: () => number;
        getEast?: () => number;
        getNorth?: () => number;
    } | null;
};

const normalizeViewport = (viewport: MapViewportSnapshot | null | undefined): MapViewportDescriptor | null => {
    const centerLat = Number(viewport?.center?.lat);
    const centerLon = Number(viewport?.center?.lng);
    const zoom = Number(viewport?.zoom);
    const west = Number(viewport?.bounds?.getWest?.());
    const south = Number(viewport?.bounds?.getSouth?.());
    const east = Number(viewport?.bounds?.getEast?.());
    const north = Number(viewport?.bounds?.getNorth?.());

    if (
        !Number.isFinite(centerLat) ||
        !Number.isFinite(centerLon) ||
        !Number.isFinite(zoom) ||
        !Number.isFinite(west) ||
        !Number.isFinite(south) ||
        !Number.isFinite(east) ||
        !Number.isFinite(north)
    ) {
        return null;
    }

    return {
        center: { lat: centerLat, lon: centerLon },
        zoom,
        bbox: [west, south, east, north],
    };
};

const viewportEquals = (left: MapViewportDescriptor | null, right: MapViewportDescriptor | null): boolean => {
    if (left === right) {
        return true;
    }
    if (!left || !right) {
        return false;
    }

    return (
        left.zoom === right.zoom &&
        left.center.lat === right.center.lat &&
        left.center.lon === right.center.lon &&
        left.bbox[0] === right.bbox[0] &&
        left.bbox[1] === right.bbox[1] &&
        left.bbox[2] === right.bbox[2] &&
        left.bbox[3] === right.bbox[3]
    );
};

type MarkerMoveDetail = {
    lat: number;
    lon: number;
};
type MarkerToggleDetail = {
    enabled: boolean;
};
type MapWindowBridgeMessage =
    | {
          source: 'stanag-map-window';
          channel: string;
          direction: 'command';
          message: MapWindowCommandMessage;
      }
    | {
          source: 'stanag-map-window';
          channel: string;
          direction: 'event';
          message: MapWindowEventMessage;
      };

const MapWindow: React.FC<MapWindowProps> = ({
    open,
    onClose,
    platforms,
    annotations = [],
    tileLayers,
    mapTileAccessToken = null,
    showDrawControls = false,
    markerPosition = null,
    markerMode = 'master',
    onMarkerMove,
    onMarkerToggle,
    onViewportChange,
}) => {
    const windowRef = useRef<Window | null>(null);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const channelListenerRef = useRef<((event: MessageEvent<MapWindowEventMessage>) => void) | null>(null);
    const popupBeforeUnloadHandlerRef = useRef<((this: Window, ev: BeforeUnloadEvent) => void) | null>(null);
    const latestOnClose = useRef(onClose);
    const latestOnMarkerMove = useRef(onMarkerMove);
    const latestOnMarkerToggle = useRef(onMarkerToggle);
    const latestOnViewportChange = useRef(onViewportChange);
    const [isPopupReady, setIsPopupReady] = useState(false);
    const closeWatchIdRef = useRef<number | null>(null);
    const hasSignaledCloseRef = useRef(false);
    const focusParentTimeoutRef = useRef<number | null>(null);
    const focusPopupTimeoutRef = useRef<number | null>(null);
    const focusFinalTimeoutRef = useRef<number | null>(null);
    const readyNotifiedRef = useRef(false);
    const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
    const isDesktopShell = useMemo(() => {
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const hasWailsRuntime =
            typeof (window as { runtime?: unknown }).runtime !== 'undefined' ||
            typeof (window as { go?: unknown }).go !== 'undefined' ||
            typeof (window as { __WAILS__?: unknown }).__WAILS__ !== 'undefined';
        return hasWailsRuntime || /wails|webview2|;\s*wv\)/i.test(userAgent) || window.location.hostname === 'wails.localhost';
    }, []);

    const channelNameRef = useRef<string>(createChannelName());
    const prevPlatformsRef = useRef<MapPlatformDescriptor[]>([]);
    const prevAnnotationsRef = useRef<MapAnnotationDescriptor[]>([]);
    const normalizedTileLayers = useMemo(() => resolveTileLayersJson(tileLayers), [tileLayers]);
    const [desktopBackendOrigin, setDesktopBackendOrigin] = useState<string | null>(null);
    const windowMessageListenerRef = useRef<((event: MessageEvent<MapWindowBridgeMessage>) => void) | null>(null);
    const directMapElementRef = useRef<DirectMapElement | null>(null);
    const directMapMarkerMoveListenerRef = useRef<((event: Event) => void) | null>(null);
    const directMapMarkerToggleListenerRef = useRef<((event: Event) => void) | null>(null);
    const lastViewportRef = useRef<MapViewportDescriptor | null>(null);
    const sanitizedTileLayers = useMemo(() => {
        const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : null;
        const baseOrigin = desktopBackendOrigin ?? fallbackOrigin;
        return sanitizeTileLayersJson(normalizedTileLayers, baseOrigin, mapTileAccessToken ?? '');
    }, [desktopBackendOrigin, mapTileAccessToken, normalizedTileLayers]);

    useEffect(() => {
        latestOnClose.current = onClose;
    }, [onClose]);

    useEffect(() => {
        latestOnMarkerMove.current = onMarkerMove;
    }, [onMarkerMove]);

    useEffect(() => {
        latestOnMarkerToggle.current = onMarkerToggle;
    }, [onMarkerToggle]);

    useEffect(() => {
        latestOnViewportChange.current = onViewportChange;
    }, [onViewportChange]);

    useEffect(() => {
        if (!isDesktopShell) {
            return;
        }

        let cancelled = false;
        const resolveDesktopBackendOrigin = async () => {
            try {
                const response = await fetch('/__desktop_meta', { cache: 'no-store' });
                if (!response.ok) {
                    return;
                }
                const payload = (await response.json().catch(() => null)) as { backendAddr?: unknown } | null;
                const backendAddr = typeof payload?.backendAddr === 'string' ? payload.backendAddr.trim() : '';
                if (!backendAddr || cancelled) {
                    return;
                }
                setDesktopBackendOrigin(`http://${backendAddr}`);
            } catch {
                // Ignore desktop metadata fetch failures and keep fallback origin.
            }
        };

        void resolveDesktopBackendOrigin();

        return () => {
            cancelled = true;
        };
    }, [isDesktopShell]);

    const clearFocusTimeouts = useCallback(() => {
        if (typeof window !== 'undefined') {
            if (focusParentTimeoutRef.current !== null) {
                window.clearTimeout(focusParentTimeoutRef.current);
            }
            if (focusPopupTimeoutRef.current !== null) {
                window.clearTimeout(focusPopupTimeoutRef.current);
            }
            if (focusFinalTimeoutRef.current !== null) {
                window.clearTimeout(focusFinalTimeoutRef.current);
            }
        }
        focusParentTimeoutRef.current = null;
        focusPopupTimeoutRef.current = null;
        focusFinalTimeoutRef.current = null;
    }, []);

    const renderDesktopPopupError = useCallback((popup: Window, error: unknown) => {
        try {
            const root = popup.document.getElementById(MAP_WINDOW_ROOT_ID);
            if (!root) {
                return;
            }
            const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
            root.innerHTML = `<div class="map-error"><h2 style="margin:0 0 12px 0;font-size:18px;">Map window failed to initialize</h2><pre>${detail}</pre></div>`;
        } catch {
            // ignore render errors
        }
    }, []);

    const ensureDesktopMapElement = useCallback(async (popup: Window): Promise<DirectMapElement | null> => {
        try {
            const doc = popup.document;
            if (!doc || !doc.body) {
                return null;
            }

            let root = doc.getElementById(MAP_WINDOW_ROOT_ID);
            if (!root) {
                doc.open();
                doc.write(buildDesktopPopupDocument());
                doc.close();
                root = doc.getElementById(MAP_WINDOW_ROOT_ID);
            }
            if (!root) {
                return null;
            }

            if (!popup.customElements.get('uav-map')) {
                const popupWindow = popup as Window & { L?: unknown };
                if (!popupWindow.L) {
                    const existingLeafletScript = doc.querySelector('script[data-map-bootstrap="leaflet"]');
                    if (!existingLeafletScript) {
                        const leafletScript = doc.createElement('script');
                        leafletScript.type = 'text/javascript';
                        leafletScript.dataset.mapBootstrap = 'leaflet';
                        leafletScript.text = leafletUmdScript;
                        doc.head.appendChild(leafletScript);
                    }
                    if (!popupWindow.L) {
                        throw new Error('Leaflet global L is unavailable after bootstrap');
                    }
                }

                const existingMapControlScript = doc.querySelector('script[data-map-bootstrap="uav-map-control"]');
                if (!existingMapControlScript) {
                    const script = doc.createElement('script');
                    script.type = 'text/javascript';
                    script.dataset.mapBootstrap = 'uav-map-control';
                    script.text = uavMapControlUmdScript;
                    doc.head.appendChild(script);
                }
            }

            if (!popup.customElements.get('uav-map')) {
                await Promise.race([
                    popup.customElements.whenDefined('uav-map'),
                    new Promise<never>((_, reject) => {
                        popup.setTimeout(() => reject(new Error('Timed out waiting for uav-map custom element')), 3000);
                    }),
                ]);
            }

            let map = root.querySelector('uav-map') as DirectMapElement | null;
            if (!map) {
                map = doc.createElement('uav-map') as DirectMapElement;
                map.style.display = 'block';
                map.style.width = '100%';
                map.style.height = '100%';
                map.SetAreaCoverageSearchVisible?.(false);
                root.appendChild(map);
            }

            return map;
        } catch (error) {
            renderDesktopPopupError(popup, error);
            return null;
        }
    }, [renderDesktopPopupError]);

    const applyCommandToDirectMap = useCallback((message: MapWindowCommandMessage) => {
        const map = directMapElementRef.current;
        if (!map) {
            return;
        }

        switch (message.type) {
            case 'initialize':
                if (message.payload.showDrawControls) {
                    map.ShowDrawControls?.();
                }
                if (typeof message.payload.tileLayers === 'string') {
                    const trimmed = message.payload.tileLayers.trim();
                    if (trimmed) {
                        map.SetTileLayers?.(trimmed);
                    }
                }
                break;
            case 'add-platform':
                map.AddPlatform?.(message.payload.id, message.payload.geojson, message.payload.data);
                break;
            case 'remove-platform':
                map.RemovePlatform?.(message.payload.id);
                break;
            case 'update-platform':
                map.UpdatePlatform?.(message.payload.id, message.payload.geojson, message.payload.data);
                break;
            case 'update-platform-data':
                map.UpdatePlatformData?.(message.payload.id, message.payload.data);
                break;
            case 'add-annotation':
                map.AddAnnotation?.(message.payload.id, message.payload.geojson);
                break;
            case 'update-annotation':
                map.UpdateAnnotation?.(message.payload.id, message.payload.geojson);
                break;
            case 'remove-annotation':
                map.RemoveAnnotation?.(message.payload.id);
                break;
            case 'set-tile-layers':
                if (typeof message.payload.tileLayers === 'string') {
                    const trimmed = message.payload.tileLayers.trim();
                    if (trimmed) {
                        map.SetTileLayers?.(trimmed);
                    }
                }
                break;
            case 'update-marker-position':
                map.UpdateMarkerPosition?.(message.payload.lat, message.payload.lon);
                break;
            case 'set-marker-master':
                map.SetMarkerMaster?.(message.payload.master);
                break;
            default:
                break;
        }
    }, []);

    const postCommand = useCallback((message: MapWindowCommandMessage) => {
        if (isDesktopShell) {
            applyCommandToDirectMap(message);
            return;
        }

        const channel = channelRef.current;
        if (channel) {
            try {
                channel.postMessage(message);
            } catch {
                // Ignore BroadcastChannel failures in desktop popup mode.
            }
        }

        const popup = windowRef.current;
        if (!popup || popup.closed) {
            return;
        }

        try {
            popup.postMessage(
                {
                    source: 'stanag-map-window',
                    channel: channelNameRef.current,
                    direction: 'command',
                    message,
                } satisfies MapWindowBridgeMessage,
                '*',
            );
        } catch {
            // Ignore postMessage failures while popup is navigating/closing.
        }
    }, [applyCommandToDirectMap, isDesktopShell]);

    const finalizePopupReady = useCallback(
        (): void => {
            if (readyNotifiedRef.current) {
                return;
            }

            readyNotifiedRef.current = true;

            clearFocusTimeouts();

            postCommand({
                type: 'initialize',
                payload: {
                    showDrawControls,
                    tileLayers: sanitizedTileLayers ?? undefined,
                },
            } satisfies MapWindowCommandMessage);

            setIsPopupReady(true);
        },
        [clearFocusTimeouts, postCommand, sanitizedTileLayers, showDrawControls],
    );

    const readCurrentViewport = useCallback((): MapViewportDescriptor | null => {
        if (isDesktopShell) {
            return normalizeViewport(directMapElementRef.current?.GetViewport?.());
        }

        const popup = windowRef.current;
        if (!popup || popup.closed) {
            return null;
        }

        try {
            const mapElement = popup.document.querySelector('uav-map') as DirectMapElement | null;
            return normalizeViewport(mapElement?.GetViewport?.());
        } catch {
            return null;
        }
    }, [isDesktopShell]);

    const activatePopupForFirefox = useCallback(
        () => {
            if (!isFirefox || typeof window === 'undefined') {
                finalizePopupReady();
                return;
            }

            clearFocusTimeouts();

            focusParentTimeoutRef.current = window.setTimeout(() => {
                try {
                    window.focus();
                    window.dispatchEvent(new Event('resize'));
                } catch {
                    // ignore focus failures
                }

                focusParentTimeoutRef.current = null;

                focusPopupTimeoutRef.current = window.setTimeout(() => {
                    const popup = windowRef.current;
                    if (popup) {
                        try {
                            popup.focus();
                            popup.dispatchEvent(new Event('resize'));
                        } catch {
                            // ignore popup focus failures
                        }
                    }

                    focusPopupTimeoutRef.current = null;

                    focusFinalTimeoutRef.current = window.setTimeout(() => {
                        focusFinalTimeoutRef.current = null;
                        finalizePopupReady();
                    }, FIREFOX_FOCUS_FINAL_DELAY_MS);
                }, FIREFOX_FOCUS_POPUP_DELAY_MS);
            }, FIREFOX_FOCUS_PARENT_DELAY_MS);
        },
        [clearFocusTimeouts, finalizePopupReady, isFirefox],
    );

    const closeWindow = useCallback((notifyPopup = true) => {
        const channel = channelRef.current;
        if (channel) {
            if (notifyPopup) {
                try {
                    channel.postMessage({ type: 'close' } satisfies MapWindowCommandMessage);
                } catch (error) {
                    // Ignore failures while closing the channel.
                }
            }
            if (channelListenerRef.current) {
                channel.removeEventListener('message', channelListenerRef.current);
                channelListenerRef.current = null;
            }
            channel.close();
            channelRef.current = null;

        }

        const popup = windowRef.current;
        if (popup) {
            if (popupBeforeUnloadHandlerRef.current) {
                try {
                    popup.removeEventListener('beforeunload', popupBeforeUnloadHandlerRef.current);
                } catch (error) {
                    // Swallow cross-origin errors that can occur if the popup navigated to a browser error page.
                }
                popupBeforeUnloadHandlerRef.current = null;
            }

            try {
                if (!popup.closed) {
                    popup.close();
                }
            } catch (error) {
                // Ignore failures closing popups that ended up on a browser error page.
            }
        }

        if (directMapElementRef.current && directMapMarkerMoveListenerRef.current) {
            directMapElementRef.current.removeEventListener('uavmap:markermove', directMapMarkerMoveListenerRef.current);
            directMapMarkerMoveListenerRef.current = null;
        }
        if (directMapElementRef.current && directMapMarkerToggleListenerRef.current) {
            directMapElementRef.current.removeEventListener('uavmap:marker-toggle', directMapMarkerToggleListenerRef.current);
            directMapMarkerToggleListenerRef.current = null;
        }

        windowRef.current = null;
        directMapElementRef.current = null;
        setIsPopupReady(false);
        lastViewportRef.current = null;
        latestOnViewportChange.current?.(null);

        readyNotifiedRef.current = false;
        clearFocusTimeouts();
        channelNameRef.current = createChannelName();

        // Reset previously synced platforms so that next open cycle re-sends
        // the desired state to the popup window.
        prevPlatformsRef.current = [];
        prevAnnotationsRef.current = [];
    }, [clearFocusTimeouts]);

    useEffect(() => {
        if (!open) {
            closeWindow();
            return;
        }

        const channelName = channelNameRef.current;

        const url = resolveMapWindowUrl();

        url.searchParams.set('channel', channelName);

        let popup = windowRef.current;
        if (!popup || popup.closed) {
            popup = window.open(isDesktopShell ? 'about:blank' : url.toString(), MAP_WINDOW_NAME, MAP_WINDOW_FEATURES);
            if (!popup) {
                console.warn('Unable to open map window.');
                latestOnClose.current?.();
                return;
            }
            windowRef.current = popup;
        } else {
            try {
                const targetUrl = url.toString();
                if (!isDesktopShell && popup.location.href !== targetUrl) {
                    popup.location.href = targetUrl;
                }
            } catch {
                if (!isDesktopShell) {
                    popup.location.href = url.toString();
                }
            }
        }

        if (isDesktopShell) {
            let cancelled = false;

            const initializeDesktopPopup = async () => {
                const mapElement = await ensureDesktopMapElement(popup!);
                if (cancelled) {
                    return;
                }
                if (!mapElement) {
                    console.warn('Unable to initialize desktop map window.');
                    return;
                }
                if (directMapElementRef.current && directMapMarkerMoveListenerRef.current) {
                    directMapElementRef.current.removeEventListener('uavmap:markermove', directMapMarkerMoveListenerRef.current);
                    directMapMarkerMoveListenerRef.current = null;
                }
                if (directMapElementRef.current && directMapMarkerToggleListenerRef.current) {
                    directMapElementRef.current.removeEventListener('uavmap:marker-toggle', directMapMarkerToggleListenerRef.current);
                    directMapMarkerToggleListenerRef.current = null;
                }
                directMapElementRef.current = mapElement;
                const handleMarkerMove = (rawEvent: Event) => {
                    const event = rawEvent as CustomEvent<MarkerMoveDetail>;
                    const lat = Number(event.detail?.lat);
                    const lon = Number(event.detail?.lon);
                    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                        return;
                    }
                    latestOnMarkerMove.current?.({ lat, lon });
                };
                const handleMarkerToggle = (rawEvent: Event) => {
                    const event = rawEvent as CustomEvent<MarkerToggleDetail>;
                    const enabled = event.detail?.enabled === true;
                    const center = enabled
                        ? (mapElement as DirectMapElement & {
                              GetCenter?: () => { lat?: number; lng?: number } | null | undefined;
                          }).GetCenter?.()
                        : null;
                    const lat = Number(center?.lat);
                    const lon = Number(center?.lng);
                    latestOnMarkerToggle.current?.({
                        enabled,
                        position: enabled && Number.isFinite(lat) && Number.isFinite(lon)
                            ? { lat, lon }
                            : null,
                    });
                };
                mapElement.addEventListener('uavmap:markermove', handleMarkerMove);
                mapElement.addEventListener('uavmap:marker-toggle', handleMarkerToggle);
                directMapMarkerMoveListenerRef.current = handleMarkerMove;
                directMapMarkerToggleListenerRef.current = handleMarkerToggle;
                activatePopupForFirefox();
            };

            void initializeDesktopPopup();

            if (!popupBeforeUnloadHandlerRef.current) {
                const handleBeforeUnload = () => {
                    latestOnClose.current?.();
                };
                popup.addEventListener('beforeunload', handleBeforeUnload);
                popupBeforeUnloadHandlerRef.current = handleBeforeUnload;
            }

            popup.focus();

            return () => {
                cancelled = true;
                closeWindow();
                if (windowMessageListenerRef.current) {
                    window.removeEventListener('message', windowMessageListenerRef.current);
                    windowMessageListenerRef.current = null;
                }
            };
        }

        let channel = channelRef.current;
        if (!channel) {
            try {
                channel = new BroadcastChannel(channelName);
                channelRef.current = channel;
            } catch {
                channel = null;
            }
        }

        if (channel && !channelListenerRef.current) {
            const handleMessage = (event: MessageEvent<MapWindowEventMessage>) => {
                const data = event.data;
                if (!data || typeof data !== 'object') {
                    return;
                }

                switch (data.type) {
                    case 'ready':
                        activatePopupForFirefox();
                        break;
                    case 'marker-move':
                        latestOnMarkerMove.current?.(data.payload);
                        break;
                    case 'marker-toggle':
                        latestOnMarkerToggle.current?.(data.payload);
                        break;
                    case 'popup-unloaded':
                        setIsPopupReady(false);
                        closeWindow(false);
                        latestOnClose.current?.();
                        break;
                    default:
                        break;
                }
            };
            channel.addEventListener('message', handleMessage);
            channelListenerRef.current = handleMessage;
        }

        if (!windowMessageListenerRef.current) {
            const handleWindowMessage = (event: MessageEvent<MapWindowBridgeMessage>) => {
                const popupWindow = windowRef.current;
                if (!popupWindow || event.source !== popupWindow) {
                    return;
                }
                const payload = event.data;
                if (!payload || typeof payload !== 'object') {
                    return;
                }
                if (payload.source !== 'stanag-map-window') {
                    return;
                }
                if (payload.direction !== 'event') {
                    return;
                }
                if (payload.channel !== channelNameRef.current) {
                    return;
                }

                if (payload.message.type === 'ready') {
                    activatePopupForFirefox();
                    return;
                }
                if (payload.message.type === 'marker-move') {
                    latestOnMarkerMove.current?.(payload.message.payload);
                    return;
                }
                if (payload.message.type === 'marker-toggle') {
                    latestOnMarkerToggle.current?.(payload.message.payload);
                    return;
                }
                if (payload.message.type === 'popup-unloaded') {
                    setIsPopupReady(false);
                    closeWindow(false);
                    latestOnClose.current?.();
                }
            };

            window.addEventListener('message', handleWindowMessage);
            windowMessageListenerRef.current = handleWindowMessage;
        }

        if (!popupBeforeUnloadHandlerRef.current) {
            const handleBeforeUnload = () => {
                latestOnClose.current?.();
            };
            popup.addEventListener('beforeunload', handleBeforeUnload);
            popupBeforeUnloadHandlerRef.current = handleBeforeUnload;
        }

        popup.focus();

        return () => {
            closeWindow();
            if (windowMessageListenerRef.current) {
                window.removeEventListener('message', windowMessageListenerRef.current);
                windowMessageListenerRef.current = null;
            }
        };
    }, [open, activatePopupForFirefox, closeWindow, ensureDesktopMapElement, isDesktopShell]);

    // Diff and sync platforms
    useEffect(() => {
        if (!open || !isPopupReady) return;
        const prev = prevPlatformsRef.current;
        const next = platforms;

        const prevMap = new Map(prev.map((p) => [p.id, p]));
        const nextMap = new Map(next.map((p) => [p.id, p]));

        // Removed platforms
        for (const prevId of prevMap.keys()) {
            if (!nextMap.has(prevId)) {
                postCommand({ type: 'remove-platform', payload: { id: prevId } } satisfies MapWindowCommandMessage);
            }
        }

        // Added / updated platforms
        for (const p of next) {
            const prevP = prevMap.get(p.id);
            if (!prevP) {
                postCommand({
                    type: 'add-platform',
                    payload: { id: p.id, geojson: p.geojson, data: p.data },
                } satisfies MapWindowCommandMessage);
                continue;
            }
            if (prevP.geojson !== p.geojson) {
                postCommand({
                    type: 'update-platform',
                    payload: { id: p.id, geojson: p.geojson, data: p.data },
                } satisfies MapWindowCommandMessage);
            } else if (prevP.data !== p.data && p.data !== undefined) {
                postCommand({
                    type: 'update-platform-data',
                    payload: { id: p.id, data: p.data },
                } satisfies MapWindowCommandMessage);
            }
        }

        prevPlatformsRef.current = next;
    }, [platforms, open, isPopupReady, postCommand]);

    useEffect(() => {
        if (!open || !isPopupReady) return;
        const prev = prevAnnotationsRef.current;
        const next = annotations;

        const prevMap = new Map(prev.map((annotation) => [annotation.id, annotation]));
        const nextMap = new Map(next.map((annotation) => [annotation.id, annotation]));

        for (const prevId of prevMap.keys()) {
            if (!nextMap.has(prevId)) {
                postCommand({ type: 'remove-annotation', payload: { id: prevId } } satisfies MapWindowCommandMessage);
            }
        }

        for (const annotation of next) {
            const prevAnnotation = prevMap.get(annotation.id);
            if (!prevAnnotation) {
                postCommand({
                    type: 'add-annotation',
                    payload: { id: annotation.id, geojson: annotation.geojson },
                } satisfies MapWindowCommandMessage);
                continue;
            }

            if (prevAnnotation.geojson !== annotation.geojson) {
                postCommand({
                    type: 'update-annotation',
                    payload: { id: annotation.id, geojson: annotation.geojson },
                } satisfies MapWindowCommandMessage);
            }
        }

        prevAnnotationsRef.current = next;
    }, [annotations, open, isPopupReady, postCommand]);

    useEffect(() => {
        if (!open || !isPopupReady) {
            return;
        }
        if (!sanitizedTileLayers) {
            return;
        }
        postCommand({
            type: 'set-tile-layers',
            payload: { tileLayers: sanitizedTileLayers },
        } satisfies MapWindowCommandMessage);
    }, [open, isPopupReady, sanitizedTileLayers, postCommand]);

    useEffect(() => {
        if (!open || !isPopupReady || !markerPosition) {
            return;
        }

        postCommand({
            type: 'update-marker-position',
            payload: markerPosition,
        } satisfies MapWindowCommandMessage);
    }, [isPopupReady, markerPosition, open, postCommand]);

    useEffect(() => {
        if (!open || !isPopupReady) {
            return;
        }

        postCommand({
            type: 'set-marker-master',
            payload: { master: markerMode === 'master' },
        } satisfies MapWindowCommandMessage);
    }, [isPopupReady, markerMode, open, postCommand]);

    useEffect(() => {
        if (!open || !isPopupReady) {
            lastViewportRef.current = null;
            latestOnViewportChange.current?.(null);
            return;
        }

        const publishViewport = () => {
            const nextViewport = readCurrentViewport();
            if (viewportEquals(lastViewportRef.current, nextViewport)) {
                return;
            }
            lastViewportRef.current = nextViewport;
            latestOnViewportChange.current?.(nextViewport);
        };

        publishViewport();

        const intervalId = window.setInterval(publishViewport, VIEWPORT_POLL_INTERVAL_MS);
        return () => {
            window.clearInterval(intervalId);
        };
    }, [isPopupReady, open, readCurrentViewport]);

    useEffect(() => closeWindow, [closeWindow]);

    // Ensure popup is closed if the main application tab/window is closed or reloaded.
    // Relying solely on component unmount isn't sufficient because React's cleanup
    // may not always execute before the browser tears down the page (e.g. hard tab close).
    // We avoid posting a 'close' command here to minimize synchronous work in the
    // beforeunload handler and just forcefully close the popup.
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Close without notifying popup (it's about to go away) to keep it synchronous.
            try {
                if (windowRef.current && !windowRef.current.closed) {
                    windowRef.current.close();
                }
            } catch {
                // ignore
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    useEffect(() => {
        if (!open) {
            if (typeof window !== 'undefined' && closeWatchIdRef.current !== null) {
                window.clearTimeout(closeWatchIdRef.current);
            }
            closeWatchIdRef.current = null;
            hasSignaledCloseRef.current = false;
            return;
        }

        if (typeof window === 'undefined') {
            return;
        }

        hasSignaledCloseRef.current = false;

        const pollClosed = () => {
            const popup = windowRef.current;
            if (!popup) {
                closeWatchIdRef.current = window.setTimeout(pollClosed, CLOSE_POLL_INTERVAL_MS);
                return;
            }

            if (popup.closed) {
                closeWatchIdRef.current = null;
                if (!hasSignaledCloseRef.current) {
                    hasSignaledCloseRef.current = true;
                    closeWindow(false);
                    latestOnClose.current?.();
                }
                return;
            }

            closeWatchIdRef.current = window.setTimeout(pollClosed, CLOSE_POLL_INTERVAL_MS);
        };

        closeWatchIdRef.current = window.setTimeout(pollClosed, CLOSE_POLL_INTERVAL_MS);

        return () => {
            if (closeWatchIdRef.current !== null) {
                window.clearTimeout(closeWatchIdRef.current);
                closeWatchIdRef.current = null;
            }
            hasSignaledCloseRef.current = false;
        };
    }, [open, closeWindow]);

    return null;
};

export default MapWindow;
